import React, { useState, useCallback, useRef } from "react";
import LocationPermissionModal from "../components/shared/LocationPermissionModal/LocationPermissionModal";

/**
 * A hook that abstracts the location permission flow.
 * It intercepts actions requiring location, checks the browser permission state,
 * and shows an educational pre-prompt or a denied-instructions modal if necessary.
 */
interface PermissionCopy {
  preTitle?: string;
  preSubtitle?: string;
  deniedSubtitle?: string;
}

const useLocationPermission = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [stage, setStage] = useState<"pre-prompt" | "denied">("pre-prompt");
  const [copy, setCopy] = useState<PermissionCopy>({});
  // Store the callbacks to execute after the user handles the modal
  const pendingActionRef = useRef<(() => void) | null>(null);
  const pendingCancelRef = useRef<(() => void) | null>(null);

  const checkPermissionAndExecute = useCallback(async (
    onSuccess: () => void,
    onCancel?: () => void,
    permissionCopy?: PermissionCopy,
  ) => {
    let permState: PermissionState | null = null;

    try {
      const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
      permState = status.state;
    } catch {
      // permissions API not supported — proceed directly and let the browser handle it
    }

    if (permState === 'denied') {
      setStage('denied');
      setCopy(permissionCopy ?? {});
      setIsOpen(true);
      pendingActionRef.current = onSuccess;
      pendingCancelRef.current = onCancel ?? null;
      return;
    }

    if (permState === 'prompt') {
      setStage('pre-prompt');
      setCopy(permissionCopy ?? {});
      setIsOpen(true);
      pendingActionRef.current = onSuccess;
      pendingCancelRef.current = onCancel ?? null;
      return;
    }

    // Permission is 'granted' or could not be determined — proceed directly
    onSuccess();
  }, []);

  const handleAllow = useCallback(() => {
    setIsOpen(false);
    pendingCancelRef.current = null;
    if (pendingActionRef.current) {
      pendingActionRef.current();
      pendingActionRef.current = null; // clean up
    }
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    pendingActionRef.current = null; // clear pending action on cancel
    if (pendingCancelRef.current) {
      pendingCancelRef.current();
      pendingCancelRef.current = null;
    }
  }, []);

  const PermissionModal = useCallback(() => (
    <LocationPermissionModal
      isOpen={isOpen}
      stage={stage}
      onAllow={handleAllow}
      onClose={handleClose}
      preTitle={copy.preTitle}
      preSubtitle={copy.preSubtitle}
      deniedSubtitle={copy.deniedSubtitle}
    />
  ), [isOpen, stage, handleAllow, handleClose, copy]);

  return {
    checkPermissionAndExecute,
    PermissionModal,
  };
};

export default useLocationPermission;
